import { useCallback, useMemo } from 'react';

import {
  LeadJourneyTimelineQuery,
  useLeadJourneyTimelineQuery,
} from 'core/graphql';

import {
  groupLeadJourney,
  ILeadJourneyDay,
  ILeadJourneyEvent,
  ILeadJourneySummary,
  leadJourneySummary,
  toLeadJourneyEvents,
} from './leadJourneyModel';
import { useLeadPermissions } from './useLeadPermissions';

/** ТЗ 0106 — путь лида с именем (вкладка «Путь» и панель «Маркетинга»). */

export type ILeadJourneyLead = NonNullable<
  LeadJourneyTimelineQuery['leadJourney']
>['lead'];

export interface IUseLeadJourney {
  lead: ILeadJourneyLead | null;
  events: ILeadJourneyEvent[];
  days: ILeadJourneyDay[];
  summary: ILeadJourneySummary | null;
  loading: boolean;
  failed: boolean;
  /** Ответ пришёл, а пути нет: лида нет в зеркале текущего источника. */
  notFound: boolean;
  reload: () => void;
}

const NO_EVENTS: ILeadJourneyEvent[] = [];
const NO_DAYS: ILeadJourneyDay[] = [];

export const useLeadJourney = (
  kommoLeadId: string | null,
  enabled = true,
): IUseLeadJourney => {
  const { canJourney } = useLeadPermissions();
  const skip = !canJourney || !kommoLeadId || !enabled;
  const { data, loading, error, refetch } = useLeadJourneyTimelineQuery({
    variables: { kommoLeadId: kommoLeadId || '' },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const journey = data?.leadJourney ?? null;
  const rawEvents = journey?.events;
  const events = useMemo(
    () => (rawEvents ? toLeadJourneyEvents(rawEvents) : NO_EVENTS),
    [rawEvents],
  );
  const days = useMemo(
    () => (events.length ? groupLeadJourney(events) : NO_DAYS),
    [events],
  );
  const summary = useMemo(() => (journey ? leadJourneySummary(events) : null), [
    journey,
    events,
  ]);
  const reload = useCallback(() => {
    refetch()?.catch(() => undefined);
  }, [refetch]);
  return {
    lead: journey?.lead ?? null,
    events,
    days,
    summary,
    loading: loading && !journey,
    failed: Boolean(error) && !journey,
    notFound: Boolean(data) && !journey,
    reload,
  };
};
